import {set} from '@coolcolduk/util';
import {Box, Stack, Typography} from '@mui/material';
import {useMemo, useState} from 'react';
import {ExamModule, StandardSubject, SubjectLevel, UserModule} from '@cp/model';
import ErrorComponent from '../../components/ErrorComponent/ErrorComponent';
import CourseAddDialog from './CourseAddDialog';
import StudyAvailableHeader from './CoursesAvailableFilters';
import StudyModuleGroup from './CoursesAvailableGroup';
import {buildManyExamModuleSelected} from './util/ExamModuleSelected';
import {filterExamModuleSelectedByAll} from './util/filterExamModuleSelected';

export interface CoursesAvailableProps {
  userModules: UserModule[];
  examModules: ExamModule[];
}

export default function CoursesAvailable({userModules, examModules}: CoursesAvailableProps) {
  const [addModuleDialogOpen, setAddModuleDialogOpen] = useState(false);
  const [selectedModuleForDialog, setSelectedModuleForDialog] = useState<ExamModule | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSubjects, setSelectedSubjects] = useState<StandardSubject[]>([]);
  const [selectedLevels, setSelectedLevels] = useState<SubjectLevel[]>([]);
  const [selectedExamBoards, setSelectedExamBoards] = useState<string[]>([]);
  const [searchFieldKey, setSearchFieldKey] = useState(0);

  // Get user's selected modules with full module details
  const examModulesWithUserModules = buildManyExamModuleSelected(examModules, userModules);

  // Apply search and filters
  const availableModules = useMemo(
    () =>
      examModulesWithUserModules.filter(
        filterExamModuleSelectedByAll(searchQuery, selectedSubjects, selectedLevels, selectedExamBoards),
      ),
    [examModulesWithUserModules, searchQuery, selectedSubjects, selectedLevels, selectedExamBoards],
  );

  // Group available modules by subject, then level, then examBoard
  const groupedModules = availableModules.reduce(
    (acc, module) => {
      const subject = module.subject || StandardSubject.MATHEMATICS;
      const level = module.level || SubjectLevel.GCSE;
      const examBoard = module.examBoard || 'All Exam Boards';

      return set(acc, `${subject}.${level}.${examBoard}`, [...(acc[subject]?.[level]?.[examBoard] || []), module]);
    },
    {} as Record<StandardSubject, Record<SubjectLevel, Record<string, ExamModule[]>>>,
  ) as Record<StandardSubject, Record<SubjectLevel, Record<string, ExamModule[]>>>;

  const handleModuleSelect = (module: ExamModule) => {
    setSelectedModuleForDialog(module);
    setAddModuleDialogOpen(true);
  };

  const handleCloseAddModuleDialog = () => {
    setAddModuleDialogOpen(false);
    setSelectedModuleForDialog(null);
  };

  const handleSubjectToggle = (subject: StandardSubject) => {
    setSelectedSubjects((prev) => (prev.includes(subject) ? prev.filter((s) => s !== subject) : [...prev, subject]));
  };

  const handleLevelToggle = (level: SubjectLevel) => {
    setSelectedLevels((prev) => (prev.includes(level) ? prev.filter((l) => l !== level) : [...prev, level]));
  };

  const handleExamBoardToggle = (examBoard: string) => {
    setSelectedExamBoards((prev) =>
      prev.includes(examBoard) ? prev.filter((e) => e !== examBoard) : [...prev, examBoard],
    );
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedSubjects([]);
    setSelectedLevels([]);
    setSelectedExamBoards([]);
    setSearchFieldKey((prev) => prev + 1);
  };

  if (examModulesWithUserModules.length === 0) {
    return (
      <ErrorComponent
        message="No courses available"
        sx={{minHeight: '60vh'}}
        // buttonText="Reload page"
        // handleClick={() => window.location.reload()}
      />
    );
  }

  return (
    <Stack data-testid="available-courses-section">
      <Typography variant="h5" sx={{fontWeight: 600, mb: 3}} data-testid="available-courses-heading">
        Available Courses
      </Typography>

      {/* Search and Filters */}
      <StudyAvailableHeader
        modules={availableModules}
        selectedSubjects={selectedSubjects}
        selectedLevels={selectedLevels}
        selectedExamBoards={selectedExamBoards}
        onSearchChange={setSearchQuery}
        onSubjectToggle={handleSubjectToggle}
        onLevelToggle={handleLevelToggle}
        onExamBoardToggle={handleExamBoardToggle}
        onClearFilters={handleClearFilters}
        searchFieldKey={searchFieldKey}
      />

      {/* Filtered Results */}
      {availableModules.length > 0 ? (
        Object.entries(groupedModules).map(([subject, levels]) => (
          <StudyModuleGroup
            key={subject}
            subject={subject as StandardSubject}
            levels={levels}
            onModuleSelect={handleModuleSelect}
          />
        ))
      ) : (
        <Box sx={{textAlign: 'center', py: 6}}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No courses match your filters
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your search or filters
          </Typography>
        </Box>
      )}

      {/* Add Module Dialog */}
      <CourseAddDialog
        open={addModuleDialogOpen}
        onClose={handleCloseAddModuleDialog}
        module={selectedModuleForDialog}
      />
    </Stack>
  );
}
